/* eslint-disable @typescript-eslint/no-unused-vars */
'use client';

import { UserInterface } from "@/types/user";
import React, { createContext, Dispatch, SetStateAction, useContext } from "react";
import Cookies from 'js-cookie';
import { GET } from "@/api/get-request";
import { NotificationInterface } from "@/types/notification";
import { signOut } from "next-auth/react";
import { usePathname, useRouter } from "next/navigation";
import dynamic from "next/dynamic";
import { POST } from "@/api/post-requests";
const Modal = dynamic(() => import('@/components/common/modal'), { ssr: false });
const Tutorial = dynamic(() => import('@/components/widgets/tutorial/tutorial'), { ssr: false });

export type CustomizationProps = {
    isauthenticated: boolean,
    loading: boolean,
    user: UserInterface | null,
    setUser: Dispatch<SetStateAction<UserInterface | null>>,
    notifications: NotificationInterface[],
    UpdateUser: () => void,
    LogOut: () => void,
    handleReadAll: () => void
};

const AuthContext = createContext<CustomizationProps | null>(null);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
    const [isauthenticated, setIsauthenticated] = React.useState<boolean>(true);
    const [notifications, setNotifications] = React.useState<NotificationInterface[]>([]);
    const [loading, setLoading] = React.useState<boolean>(true);
    const [user, setUser] = React.useState<UserInterface | null>(null);
    const route = useRouter();
    const [open, setOpen] = React.useState(user?.need_tutorial ? true : false);
    const pathname = usePathname();
    const loadingRef = React.useRef<boolean>(loading);
    const handleClose = () => {
        setOpen(false);
        POST({ url: 'v1/profile/tutorial', body: { need_tutorial: false } })
            .then(response => {
                if (response.success) {
                    setUser(prev => prev ? { ...prev, need_tutorial: false } : null);
                }
            })
            .catch(error => {
                console.error("Error updating tutorial status:", error);
            })
    };

    const GetNotifications = async () => {
        const response = await GET({ url: 'v1/notifications' });
        if (response.success)
            setNotifications(response.data);
    };


    const handleReadAll = async () => {
        const response = await POST({ url: 'v1/notifications/read-all', body: {} });
        if (response.success)
            GetNotifications()
    };

    const UpdateUser = async () => {
        const response = await GET({ url: 'v1/profile' });
        if (response.success) {
            setUser(response.data);
        }
    };

    const LogOut = () => {
        Cookies.remove('token');
        signOut({ callbackUrl: '/login' });
        route.replace('/login');
    };

    React.useEffect(() => {
        GetNotifications();
        setOpen(!!user?.need_tutorial);
    }, [user]);

    React.useEffect(() => {
        loadingRef.current = loading;
    }, [loading]);

    React.useLayoutEffect(() => {

        const CheckUser = async () => {
            const startedAt = Date.now();
            if (typeof console.time === 'function') {
                console.time('[AUTH] GET v1/profile');
            }
            try {
                setLoading(true);
                const token = Cookies.get('token');
                if (token) {
                    const response = await GET({ url: 'v1/profile' });
                    if (response.success) {
                        setUser(response.data);
                        GetNotifications();
                        console.log('[AUTH] profile loaded', { durationMs: Date.now() - startedAt, status: 'success' });
                    } else {
                        setUser(null);
                        console.warn('[AUTH] profile API returned failure', { durationMs: Date.now() - startedAt, response });
                    }
                } else {
                    setUser(null);
                    console.log('[AUTH] no token found');
                }
            } catch (error) {
                console.error('[AUTH] profile fetch failed', { durationMs: Date.now() - startedAt, error });
            } finally {
                if (typeof console.timeEnd === 'function') {
                    console.timeEnd('[AUTH] GET v1/profile');
                }
                setLoading(false);
            }
        };

        const watchdog = setTimeout(() => {
            if (loadingRef.current) {
                console.warn('[AUTH] loading still true after 4000ms');
            }
        }, 4000);


        CheckUser();
        return () => clearTimeout(watchdog);
    }, []);

    return <AuthContext.Provider
        value={{
            isauthenticated,
            user,
            loading,
            setUser,
            notifications,
            UpdateUser,
            LogOut,
            handleReadAll
        }}>
        {children}
        {pathname?.includes('home') &&
            <Modal
                open={open}
                handleClose={handleClose}
            >
                <Tutorial handleClose={handleClose} />
            </Modal>
        }
    </AuthContext.Provider>
};


export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};
